import type {
  PublicationConfirmation,
  PublicationPreview,
} from '@/types/automationPublications.ts';

export type PendingPublication = {
  request: PublicationConfirmation;
  preview: PublicationPreview;
  // Unknown outcomes may recover a committed result even after token expiry.
  submitted: boolean;
};
export const publicationRecoveryKey = (scope: readonly string[], releaseId: string) =>
  JSON.stringify(['publication-confirmation', ...scope, releaseId]);
export function readPublicationRecovery(key: string, releaseId: string): PendingPublication | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) ?? 'null') as PendingPublication | null;
    const plan = value?.preview?.plan as (PublicationPreview['plan'] & { contract_warnings?: unknown }) | undefined;
    if (
      !value ||
      !plan ||
      plan.schema_version !== 'publication_plan.v2' ||
      plan.release?.release_id !== releaseId ||
      !['activate', 'rollback'].includes(plan.action) ||
      !Array.isArray(plan.targets) ||
      !plan.targets.length ||
      plan.targets.some(
        (target) => !target?.before?.automation_id || !Array.isArray(target.roots),
      ) ||
      !Array.isArray(plan.findings) ||
      !Array.isArray(plan.shared_impacts) ||
      value.request?.schema_version !== 'automation_activation_apply.v1' ||
      value.request.preview_token !== value.preview.preview_token ||
      value.request.preview_digest !== value.preview.preview_digest ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(
        value.request.request_id,
      ) ||
      typeof value.submitted !== 'boolean' ||
      typeof value.request.confirm_documentation !== 'boolean'
    )
      return null;
    // Pre-warning previews can still be recovered. They did not contain an
    // advisory finding, so normalize the missing field to its contract default.
    if (plan.contract_warnings === undefined) plan.contract_warnings = [];
    else if (!Array.isArray(plan.contract_warnings)) return null;
    return value;
  } catch {
    return null;
  }
}
export function retainPublication(key: string, value: PendingPublication | null) {
  try {
    if (value) sessionStorage.setItem(key, JSON.stringify(value));
    else sessionStorage.removeItem(key);
  } catch {
    /* In-memory recovery remains available. */
  }
}
